import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@proptr/api-client/react';
import { createClient } from '@proptr/api-client';
import { ThemeProvider } from '@proptr/ui';
import './index.css';
import { App } from './App.js';

const IMP_TOKEN_KEY = 'proptr_imp_token';

// When a manager opens this app via "Login as Proptree", the URL carries
// `?_imp=<jwt>`. Move it into sessionStorage (tab-scoped — no leak to
// other tabs, no leak to the cookie jar) and strip it from the URL
// before React mounts. Closing the tab cleanly ends the impersonation.
// Mirrors the manager portal's admin-impersonation handling pattern.
(function captureImpersonationToken() {
  try {
    const url = new URL(window.location.href);
    const imp = url.searchParams.get('_imp');
    if (imp) {
      window.sessionStorage.setItem(IMP_TOKEN_KEY, imp);
      url.searchParams.delete('_imp');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    // sessionStorage unavailable (private mode etc.) — fall through; the
    // app will just not have an impersonation session.
  }
})();

export const client = createClient({
  baseUrl: import.meta.env.VITE_API_URL ?? '',
  // Read on every request — sessionStorage is the source of truth for the
  // current tab's impersonation state. Returning null falls back to the
  // normal cookie auth (none, in Phase 1).
  getBearerToken: () => {
    try {
      return window.sessionStorage.getItem(IMP_TOKEN_KEY);
    } catch {
      return null;
    }
  },
});

// Exposed so the impersonation banner's "Exit" handler can clear it.
export function clearImpersonationToken(): void {
  try {
    window.sessionStorage.removeItem(IMP_TOKEN_KEY);
  } catch {
    // ignore
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider client={client}>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>,
);
